import { Edge, Node } from "@xyflow/react";
import { SequenceNodeData } from "./SequenceNode";
import Interaction, {
  InteractionType,
} from "../../models/interactions/Interaction";
import { ISubSequence } from "../../models/interactions/SubSequence";
import {
  ITrigger,
  TriggerType,
  getTriggerLabel,
} from "../../models/interactions/Trigger";

export function isTakeSequenceEditor(selectedElementType?: string) {
  return selectedElementType === "takeSequence";
}

export function isProtectedSequenceStructureInteraction(
  interaction: Interaction,
  selectedElementType?: string,
) {
  if (interaction.type === InteractionType.Sequence) return true;
  if (
    !isTakeSequenceEditor(selectedElementType) ||
    interaction.type !== InteractionType.Trigger
  ) {
    return false;
  }

  const triggerType = (interaction as ITrigger).triggerType;
  return (
    triggerType === TriggerType.OnStart || triggerType === TriggerType.OnEnd
  );
}

export function isSequenceRootNode(node: Node<SequenceNodeData>) {
  return node.data?.interaction?.type === InteractionType.Sequence;
}

export function isTakeSequenceGroupNode(node: Node<SequenceNodeData>) {
  const type = node.data?.interaction?.type;
  return (
    type === InteractionType.SubSequence || type === InteractionType.Trigger
  );
}

export function isProtectedTakeSequenceTrigger(
  node: Node<SequenceNodeData>,
  selectedElementType?: string,
) {
  if (!node.data?.interaction) return false;
  return isProtectedSequenceStructureInteraction(
    node.data.interaction,
    selectedElementType,
  );
}

export function getTakeSequenceGroupLabel(node: Node<SequenceNodeData>) {
  if (node.data?.interaction?.type === InteractionType.Trigger) {
    return getTriggerLabel((node.data.interaction as ITrigger).triggerType);
  }

  if (node.data?.interaction?.type === InteractionType.SubSequence) {
    return (node.data.interaction as ISubSequence).sequenceName;
  }

  return node.data?.name ?? node.data?.interaction?.name ?? node.id;
}

export function getVisibleSequenceNodes(
  nodes: Node<SequenceNodeData>[],
  selectedElementType?: string,
) {
  if (!isTakeSequenceEditor(selectedElementType)) {
    return nodes;
  }

  return nodes.filter((node) => !isSequenceRootNode(node));
}

export function getVisibleSequenceEdges(
  nodes: Node<SequenceNodeData>[],
  edges: Edge[],
  selectedElementType?: string,
) {
  const visibleNodeIds = new Set(
    getVisibleSequenceNodes(nodes, selectedElementType).map((node) => node.id),
  );

  return edges.filter(
    (edge) =>
      visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target),
  );
}
